<style lang="scss" scoped>
    .helpful-hints {
        a {
            cursor: pointer;
        }
    }
</style>

<template>
    <div class="page-container">
        <h1>Enrollment</h1>

        <section-collapse accordion="enrollment-accordion" id="enrollment-why-its-important">
            <template #title>Why it's Important</template>
            <template #collapsed-content>
                <p>
                    Enrollment is the first step to making Go NAPSACC a success in your community. As the Consultant, you share information about Go NAPSACC with local child care providers and encourage their participation.
                </p>

                <p>
                    You are the ideal ambassador for Go NAPSACC because you know the child care providers in your community, and they know you as a trusted source of guidance and support. Some child care providers may see the benefit of participation immediately, while others may be more hesitant. Encourage participation by letting them know what to expect, assuring them of your ongoing support, and sharing success stories from other participants.
                </p>

                <div class="callout-dotted-border">
                    When Go NAPSACC surveyed child care providers about why they chose to participate, the most common reasons were knowing they had room for improvement related to nutrition and physical activity and wanting a resource for support (33%), having a focus on nutrition and physical activity already and seeing Go NAPSACC as a good fit (29%), or having a trusted organization or consultant suggest they participate (19%).
                </div>
            </template>
        </section-collapse>

        <section-collapse accordion="enrollment-accordion" id="enrollment-activities">
            <template #title>Activities</template>
            <template #collapsed-content>
                <p>Click any activity to read more</p>

                <activity-collapse>
                    <template #number>1</template>
                    <template #title>
                        <strong>Consultant</strong> distributes promotional materials
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>When you are ready to start enrolling child care providers, use multiple strategies to promote Go NAPSACC and encourage participation. Distribute promotional messages widely through <span class="text-warning"><strong>listservs</strong></span>, <span class="text-warning"><strong>newsletters</strong></span>, <span class="text-warning"><strong>social media</strong></span>, <span class="text-warning"><strong>flyers</strong></span>, or <span class="text-warning"><strong>letters</strong></span>. Another strategy is to provide a <span class="text-warning"><strong>teaser presentation</strong></span> about Go NAPSACC during trainings or other in-person informational sessions where providers from several child care programs are already gathered.</p>
                        </div>

                        <helpful-hints>
                            <h5>Accentuate the positive</h5>

                            <video-modal :video="videoInfo" v-model="showModalInfoVideo"></video-modal>

                            <p>Messages should highlight benefits that child care providers can expect. Include the link to <a class="text-warning" @click="showModalInfoVideo = !showModalInfoVideo"><strong>Go NAPSACC’s informational video</strong></a> so they can hear first-hand from other child care providers. If available, share success stories from past participants in your community or local statistics about children’s health.</p>

                            <h5>Be clear about expectations</h5>

                            <video-modal :video="videoOverview" v-model="showModalOverviewVideo"></video-modal>

                            <p>Make sure that child care providers understand what is required for participation. It may be helpful to include a link to <a class="text-warning" @click="showModalOverviewVideo = !showModalOverviewVideo"><strong>Go NAPSACC’s 5-step overview video</strong></a>.</p>

                            <h5>Let them know it’s free</h5>
                            <p>Some child care providers may be worried that there is a catch to participation. Reassure them that they will have full access to Go NAPSACC at no cost to them.</p>

                            <h5>Be mindful of any eligibility requirements</h5>
                            <p>Some states may need to prioritize enrollment of certain child care programs (examples: those that accept subsidies or have a certain quality rating). Be aware of these requirements so you can focus your efforts toward eligible child care programs and providers.</p>

                            <h5>Avoid busy times of year</h5>
                            <p>Adjust the timing of your enrollment efforts. Avoid busy months like August and September when child care programs are kicking off their new school year. Instead, target January or summer months when they are more able and motivated.</p>

                            <h5>Host an information session</h5>
                            <p>Watch for natural opportunities when a group of child care providers from different programs are already gathered. Customize your presentation and materials for your audience. Bring extra flyers and ask attendees to help you spread the word. For child care providers that express interest, gather contact information and schedule follow-up meetings.</p>

                            <h5>Offer incentives or recognition</h5>
                            <ul>
                                <li>Allow Go NAPSACC participation to count for continuing education or contact hour credits.</li>
                                <li>Provide mini-grants to cover improvements needed to meet best practice goals or offer financial incentives for the purchase of new materials</li>
                                <li>Recognize child care programs for their participation and achievements through press releases, social media, or an end-of year celebration. Banners or a framed certificate for display can also help child care programs advertise their hard work to prospective families.</li>
                            </ul>
                        </helpful-hints>

                    </template>
                </activity-collapse>
                <activity-collapse>
                    <template #number>2</template>
                    <template #title>
                        <strong>Consultant</strong> conducts personal follow-ups
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>Personal follow-ups are often needed to help child care providers understand the benefits of participation. These follow-ups can be made by email, phone, or in-person visits. These one-on-one communications allow you to explain what Go NAPSACC has to offer, what is required for participation, and the application process (if any). Bring your laptop or sample materials to showcase the tools and resources from Go NAPSACC you think are most appealing.</p>
                        </div>

                        <div class="callout-dotted-border">
                                During outreach, MaryAnne Candley (Go NAPSACC Consultant, Louisiana) conducts visits with her Go NAPSACC binder, noting that "giving centers visual ideas of information they can share with their families and tools they can implement in their classrooms really seems to get directors and owners excited about the program.”
                            Key resources she includes are:
                            <ul>
                                <li>Sample menus</li>
                                <li><a :href="$basePath + '/uploads/build_a_healthy_plate_with_vegetables.pdf'" target="_blank" class="text-warning"><strong>Build a Healthy Plate with Vegetables</strong></a> and similar resources</li>
                                <li><a :href="$basePath + '/uploads/gross_motor_visual_aid_cards.pdf'" target="_blank" class="text-warning"><strong>Gross Motor Visual Aid Cards</strong></a> especially the yoga cards</li>
                                <li><a :href="$basePath + '/uploads/move_more_its_in_the_cards.pdf'" target="_blank" class="text-warning"><strong>Move More It’s in the Cards</strong></a> quick and simple activity ideas</li>
                                <li>Family engagement resources (Content Coming Soon)</li>
                            </ul>
                        </div>

                    </template>
                </activity-collapse>
                <activity-collapse>
                    <template #number>3</template>
                    <template #title>
                        Interested <strong>child care providers</strong> identify a key contact person for their program
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>Before child care providers commit to participation, it’s important to make sure that they know what they are signing up for. Be sure to review what is involved, including whether certain activities are tied to incentives. It is best to do this in-person or by phone so you can answer questions and address any concerns. Details may be missed or misinterpreted if you rely solely on email or text.</p>
                            <p>Once a child care provider confirms that they want to participate, they must designate at least one key contact person to represent their program. Go NAPSACC is a quality improvement program for the whole child care facility, not individual classrooms. As such, it is important for the key contact to be a director, owner, or other staff member who can represent the child care program. Collect the key contact’s preferred email address so that you can invite them to register for a Go NAPSACC account through your Consultant Tools. Also, collect a phone number for later follow-up.</p>
                        </div>
                    </template>
                </activity-collapse>
                <activity-collapse>
                    <template #number>4</template>
                    <template #title>
                        <strong>Consultant</strong> invites child care providers to register for a Go NAPSACC account
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>Invite child care providers identified as a key contact to register using your Consultant Tools.</p>
                        </div>
                        <consultant-tools-spotlight>
                            <div class="collapsed-content">
                                <h4>To invite new programs...</h4>
                                <ol>
                                    <li>Go to your Connections page.</li>
                                    <li>Use the form at the bottom to invite child care programs’ key contacts to register for an account.</li>
                                    <li>Monitor completed registrations on your My Programs page.</li>
                                </ol>

                                <slot>
                                    <img :src="$basePath + '/images/ta-resources/enrollment/consultant-spotlight-invite-new-programs.png'" alt="consultant-spotlight-invite-new-programs" class="w-100">
                                </slot>
                            </div>
                        </consultant-tools-spotlight>

                    </template>
                </activity-collapse>
                <activity-collapse>
                    <template #number>5</template>
                    <template #title>
                        <strong>Child care providers</strong> complete registration
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>Child care providers use the emailed invitation to register for a Go NAPSACC account. The email address they enter during registration becomes their username. They also create their own password. The registration process captures basic information about their child care program that is used to customize their Go NAPSACC experience.</p>
                        </div>
                        <helpful-hints>
                            <h5>Monitor progress</h5>
                            <p>You can monitor completed registrations on your My Programs page. If child care providers’ use your email invitation to complete registration, they automatically appear as one of your connected programs.</p>

                            <h5>Help with passwords</h5>
                            <p>Passwords are often forgotten, and child care providers are not able to access their account without it. Encourage them to pick something that is easy to remember and to record it in a notebook or on their phone. If passwords are forgotten, they can use the Reset Password link available on the Log In page.</p>

                            <h5>Adjust for comfort with technology</h5>
                            <p>Those who are comfortable with technology can register on their own. However, others may need your help. If needed, you can incorporate registration into the orientation session.</p>

                            <h5>Allow child care programs to designate multiple key contacts</h5>
                            <p>Child care programs can designate multiple key contacts if desired. You will need to invite all key contacts to register for an account. Once the first is registered, the website will remember the name of the child care program. When the next goes to register and they begin entering the program name, the website will try to fill in the remaining information. They just need to be sure that all information, like the child care program name and location, is correct.</p>
                        </helpful-hints>
                    </template>
                </activity-collapse>
            </template>
        </section-collapse>

        <section-collapse accordion="enrollment-accordion" id="enrollment-common-challenges">
            <template #title>Common Challenges &amp; Solutions</template>
            <template #collapsed-content>
                <h4 class="mt-0">Technology difficulties</h4>
                <p>Technology can be a barrier, especially for those who are less comfortable with computers or reside in areas with limited access to the Internet. To address technology challenges, try the following strategies.</p>
                <ul>
                    <li>Reassure child care providers that you will show them how to use all the online tools. Orientation includes a demonstration of the tools and time for hands-on learning. Assure child care providers that you will be there to support them as they learn the tools and move through the process. The tools are designed to be user friendly, so most child care providers learn to navigate them quickly.</li>
                    <li>Explore options for offering computer and internet access. Your organization/office may be able to set up a computer for child care providers to drop in and use. Local libraries are another great option as they are located throughout the community and frequently offer free access to computers with internet.</li>
                    <li>Let them know that some steps, like registration and self-assessment, can be completed on a mobile phone. However, action planning and tips &amp; materials are harder to read and use in this small format.</li>
                </ul>
                <p>If none of these options help, your organization or state Go NAPSACC Coordinator may consider adding
                    requirements for participation, such as willingness to use the online tools and/or a reliable
                    internet connection. Some Consultants have created accounts for child care providers, printed out
                    tools for them to complete by hand, and then entered the information into their accounts. This
                    approach requires more time from the Consultant, which may not be possible to maintain.</p>

                <h4>Lack of child care providers interest</h4>
                <p>Be persistent and look for opportunities for face-to-face meetings where you can ask about and address any perceived challenges. While it is important to accentuate the positives, be clear about what participation requires. Ultimately, it will be harder to keep child care providers engaged if they are not ready or willing to participate.</p>
                <p>Giving an estimate of the time required can help them assess whether they are able to participate. In general, Go NAPSACC requires about 1 hour per month. Below are time estimates for each step.</p>
                <ul>
                    <li>Orientation: 45-60 minutes</li>
                    <li>Self-Assessment: 10-40 minutes each, depending on the module</li>
                    <li>Goal Setting &amp; Action Planning: 5-10 minutes for goal setting, plus 10-15 minutes per goal for action planning</li>
                    <li>Taking Action: ~1 hour per month, depending on the goal</li>
                    <li>Repeating Self-Assessment: 10-40 minutes each, depending on the module</li>
                    <li>Note: Times vary depending on level of experience and comfort using the tools. They also fluctuate depending on whether child care providers are completing steps independently or during meetings with their Consultant.</li>
                </ul>

                <h4>Non-English speaking</h4>
                <p>Go NAPSACC is currently available in English. Some paper-based Spanish-language resources are available, such as self-assessments and certain tips &amp; materials. Consultants who speak both English and the child care provider’s native language may be able to help interpret tools and guide them through the Go NAPSACC process. Tools like Google Translate may also be useful but may not always provide the ideal translation.</p>

                <h4>Lost email invitations</h4>
                <p>First, check the following:</p>
                <ul>
                    <li>Was the email address used in the invitation form typed correctly?</li>
                    <li>Was the email address used in the invitation form one that the child care provider checks regularly?</li>
                    <li>Did the invitation go into their spam folder?</li>
                </ul>

                <p>When you send a registration invitation, the email appears to come Go NAPSACC. Using the Go NAPSACC email address should help it stand out.  If a child care providers replies to the Go NAPSACC email, it gets redirected to you so that you can respond directly to any questions.</p>

                <h4>Incorrect information entered during registration</h4>
                <p>It’s good to correct registration information as soon as possible, because that information is used to customize self-assessments and action plans. You can review the information that they entered by clicking on the name of the child care program from your My Programs page. While you cannot change this information, you should encourage child care providers to correct the information on their My Account page.</p>

            </template> 
        </section-collapse>

        

        <section-collapse accordion="enrollment-accordion" id="enrollment-support-documents">
            <template #title>Support Documents</template>
            <template #collapsed-content>
                <slot name="support-docs">
                </slot>
            </template>
        </section-collapse>
    </div>
</template>

<script>
    import VideoModal from '../../materials/VideoModal';
    import SectionCollapse from '../../TaResources/SectionCollapse.vue';
    import HelpfulHints from '../../TaResources/HelpfulHints.vue';
    import ActivityCollapse from '../../TaResources/ActivityCollapse.vue';
    import ConsultantToolsSpotlight from '../../TaResources/ConsultantToolsSpotlight.vue';

    export default {
        props: {
            videoInfo: {
                type: Object,
                default: () => {
                    return {
                        title: 'Go NAPSACC’s Informational Video',
                        assessmentURL: 'https://www.youtube.com/embed/qUiSK5K7Qrs?ecver=2&modestbranding=1&autoplay=0&showinfo=0&enablejsapi=1&rel=0'
                    }
                }
            },
            videoOverview: {
                type: Object,
                default: () => {
                    return {
                        title: 'Go NAPSACC’s 5-step Overview Video',
                        assessmentURL: 'https://www.youtube.com/embed/EEcdw7f-ebg?ecver=2&modestbranding=1&autoplay=0&showinfo=0&enablejsapi=1&rel=0'
                    }
                }
            }
        },
        components: {
            VideoModal,
            SectionCollapse,
            HelpfulHints,
            ActivityCollapse,
            ConsultantToolsSpotlight,
        },
        data() {
            return {
                showModalInfoVideo: false,
                showModalOverviewVideo: false
            }
        },
        methods: {}
    }
</script>